import React, {useEffect, useState} from 'react';
import {Box, Typography} from '@material-ui/core';
import {Skeleton} from '@material-ui/lab';

import {
  apiGetSupportCustomers,
  apiFarmerLinkedAccounts,
  apiGetAccountStatement,
} from 'constants/Api';
import {IWalletMetrics, IWalletMetricsPeriod} from 'constants/interfaces';

import './WalletMetricsWidget.scss';

function formatDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    '0',
  )}-${String(d.getDate()).padStart(2, '0')}`;
}

function dateRangeForPeriod(period: IWalletMetricsPeriod): {
  startDate: string;
  endDate: string;
} {
  const today = new Date();
  const start = new Date(today);

  if (period === 'WEEK') start.setDate(today.getDate() - 7);
  else if (period === 'MONTH') start.setMonth(today.getMonth() - 1);
  else start.setFullYear(today.getFullYear() - 1);

  return {startDate: formatDate(start), endDate: formatDate(today)};
}

export default function WalletMetricsWidget(): React.ReactElement {
  const [period, setPeriod] = useState<IWalletMetricsPeriod>('MONTH');
  const [metrics, setMetrics] = useState<IWalletMetrics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);

    const {startDate, endDate} = dateRangeForPeriod(period);

    apiGetSupportCustomers()
      .then(async (customers) => {
        const farmerIds = customers
          .map((c) => c.farmer?.id)
          .filter((id): id is number => !!id);

        const accountsPerFarmer = await Promise.all(
          farmerIds.map(async (farmerId) => {
            try {
              const linkedAccounts = await apiFarmerLinkedAccounts(
                String(farmerId),
              );
              return linkedAccounts.flatMap((la) => la.accounts ?? []);
            } catch (err) {
              console.error(
                `Failed to load linked accounts for farmer ${farmerId}`,
                err,
              );
              return [];
            }
          }),
        );

        const accounts = accountsPerFarmer.flat();

        const statements = await Promise.all(
          accounts.map(async (account) => {
            try {
              return await apiGetAccountStatement(
                String(account.id),
                account.accountType,
                startDate,
                endDate,
              );
            } catch (err) {
              console.error(
                `Failed to load statement for account ${account.id}`,
                err,
              );
              return null;
            }
          }),
        );

        if (!alive) return;

        const totalBalance = accounts.reduce(
          (sum, a) => sum + (a.AvailableBalance || 0),
          0,
        );

        let totalSpend = 0;
        let transactionCount = 0;
        statements.forEach((statement) => {
          const items = statement?.Statement ?? [];
          transactionCount += items.length;
          totalSpend += items
            .filter((tx) => tx.Amount < 0)
            .reduce((sum, tx) => sum + Math.abs(tx.Amount), 0);
        });

        setMetrics({
          totalWallets: accounts.length,
          totalBalance,
          totalSpend,
          transactionCount,
        });
      })
      .catch((err) => {
        console.error('Failed to load wallet metrics for widget', err);
        if (alive) setMetrics(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [period]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <Box className="wallet-metrics-widget">
      <Box className="widget-header">
        <Typography className="widget-heading">WALLET METRICS</Typography>
        <select
          className="period-select"
          value={period}
          onChange={(e) => setPeriod(e.target.value as IWalletMetricsPeriod)}
        >
          <option value="WEEK">This Week</option>
          <option value="MONTH">This Month</option>
          <option value="YEAR">This Year</option>
        </select>
      </Box>

      {loading ? (
        <Skeleton variant="rect" height="100%" style={{borderRadius: 8}} />
      ) : !metrics ? (
        <Typography variant="body2" color="textSecondary">
          Could not load wallet metrics.
        </Typography>
      ) : (
        <Box className="metrics-grid">
          <Box className="metric-card">
            <Typography className="metric-value">
              {metrics.totalWallets.toLocaleString()}
            </Typography>
            <Typography className="metric-label">Total Wallets</Typography>
          </Box>
          <Box className="metric-card">
            <Typography className="metric-value">
              {formatCurrency(metrics.totalBalance)}
            </Typography>
            <Typography className="metric-label">Total Balance</Typography>
          </Box>
          <Box className="metric-card">
            <Typography className="metric-value">
              {formatCurrency(metrics.totalSpend)}
            </Typography>
            <Typography className="metric-label">Total Spend</Typography>
          </Box>
          <Box className="metric-card">
            <Typography className="metric-value">
              {metrics.transactionCount.toLocaleString()}
            </Typography>
            <Typography className="metric-label">Transactions</Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}
